import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Alert,
    Button,
    Col,
    Form,
    Row,
    Spinner,
} from "react-bootstrap";
import authService from "../../../utils/authService";
import { useNavigate } from "react-router-dom";
import { useTestRoutes } from "./testRoutes";
import TestFilterSelect from "./TestFilterSelect";
import TestMultiSelect from "./TestMultiSelect";
import { fetchAllTracksForCourses } from "../../../utils/trackUtils";
import {
    fetchCoursesByTrack,
    fetchSubjectsOfCourses,
    type CourseSubjectOption,
} from "../../../utils/courseUtils";
import { buildAutoTestName } from "../../../constants/testConstants";
import {
    fetchCreateableTestTypes,
    fetchNextTestNameSequence,
    type CreateableTestTypeItem,
} from "./testUtils";
import { TextField } from "@mui/material";

const CreateTestForm: React.FC = () => {
    const [testType, setTestType] = useState("");
    const [trackId, setTrackId] = useState("");
    const [courseId, setCourseId] = useState("");
    const [subjectIds, setSubjectIds] = useState<string[]>([]);
    const [nameSequence, setNameSequence] = useState<number | null>(null);
    const [loading, setLoading] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");

    const [createableTypes, setCreateableTypes] = useState<
        CreateableTestTypeItem[]
    >([]);
    const [typesLoading, setTypesLoading] = useState(false);
    const [tracks, setTracks] = useState<
        Awaited<ReturnType<typeof fetchAllTracksForCourses>>
    >([]);
    const [courses, setCourses] = useState<
        Awaited<ReturnType<typeof fetchCoursesByTrack>>
    >([]);
    const [subjects, setSubjects] = useState<CourseSubjectOption[]>([]);
    const [tracksLoading, setTracksLoading] = useState(false);
    const [coursesLoading, setCoursesLoading] = useState(false);
    const [subjectsLoading, setSubjectsLoading] = useState(false);

    const navigate = useNavigate();
    const routes = useTestRoutes();
    const courseIds = useMemo(
        () => (courseId ? [courseId] : []),
        [courseId]
    );
    const courseIdsKey = courseId;
    const courseIdsKeyRef = useRef(courseIdsKey);
    courseIdsKeyRef.current = courseIdsKey;

    const isFinalTest = testType === "Final Test";
    const isOverallFinalTest = testType === "Overall Final Test";
    const selectedTypeMeta = useMemo(
        () => createableTypes.find((row) => row.test_type === testType) || null,
        [createableTypes, testType]
    );
    const subjectOptions = useMemo(
        () =>
            subjects.map((s) => ({
                value: s.subject_id,
                label: s.subject_name,
            })),
        [subjects]
    );
    const courseOptions = useMemo(
        () =>
            courses.map((c) => ({
                value: c.course_id,
                label: c.course_name,
            })),
        [courses]
    );
    const courseNameById = useMemo(
        () => new Map(courses.map((c) => [c.course_id, c.course_name])),
        [courses]
    );
    const subjectNameById = useMemo(
        () => new Map(subjects.map((s) => [s.subject_id, s.subject_name])),
        [subjects]
    );

    const previewName = useMemo(() => {
        if (
            !selectedTypeMeta ||
            !nameSequence ||
            courseIds.length === 0 ||
            subjectIds.length === 0
        ) {
            return "";
        }
        const courseNames = courseIds
            .map((id) => courseNameById.get(id))
            .filter((name): name is string => Boolean(name));
        const subjectNames = subjectIds
            .map((id) => subjectNameById.get(id))
            .filter((name): name is string => Boolean(name));
        if (
            courseNames.length !== courseIds.length ||
            subjectNames.length !== subjectIds.length
        ) {
            return "";
        }
        return buildAutoTestName(
            selectedTypeMeta.prefix,
            courseNames,
            subjectNames,
            nameSequence
        );
    }, [
        selectedTypeMeta,
        nameSequence,
        courseIds,
        subjectIds,
        courseNameById,
        subjectNameById,
    ]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setTypesLoading(true);
            setTracksLoading(true);
            try {
                const [types, trackData] = await Promise.all([
                    fetchCreateableTestTypes(),
                    fetchAllTracksForCourses(),
                ]);
                if (!cancelled) {
                    setCreateableTypes(types);
                    setTracks(trackData);
                }
            } catch (err) {
                console.error("Failed to fetch create options:", err);
                if (!cancelled) {
                    setCreateableTypes([]);
                    setTracks([]);
                }
            } finally {
                if (!cancelled) {
                    setTypesLoading(false);
                    setTracksLoading(false);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        setCourseId("");
        setSubjectIds([]);
        setSubjects([]);
        setNameSequence(null);
        if (!trackId) {
            setCourses([]);
            return;
        }
        let cancelled = false;
        (async () => {
            setCoursesLoading(true);
            try {
                const courseData = await fetchCoursesByTrack(trackId);
                if (!cancelled) setCourses(courseData);
            } catch (err) {
                console.error("Failed to load track courses:", err);
                if (!cancelled) setCourses([]);
            } finally {
                if (!cancelled) setCoursesLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [trackId]);

    useEffect(() => {
        setSubjectIds([]);
        if (!courseId) {
            setSubjects([]);
            setNameSequence(null);
            return;
        }
        let cancelled = false;
        (async () => {
            setSubjectsLoading(true);
            try {
                const subjectData = await fetchSubjectsOfCourses([courseId]);
                if (!cancelled) setSubjects(subjectData);
            } catch (err) {
                console.error("Failed to load subjects for courses:", err);
                if (!cancelled) setSubjects([]);
            } finally {
                if (!cancelled) setSubjectsLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [courseId]);

    // Overall Final: always include every subject on the selected course (locked).
    useEffect(() => {
        if (!isOverallFinalTest) return;
        const allIds = subjects.map((s) => s.subject_id).filter(Boolean);
        setSubjectIds(allIds);
    }, [isOverallFinalTest, subjects]);

    // Sequence only after subjects are ready — avoids calls while courses are still being toggled.
    useEffect(() => {
        if (!testType || !courseIdsKey || subjectIds.length === 0) {
            setNameSequence(null);
            return;
        }
        let cancelled = false;
        const timer = window.setTimeout(async () => {
            try {
                const sequence = await fetchNextTestNameSequence(testType, [
                    courseIdsKey,
                ]);
                if (!cancelled && courseIdsKeyRef.current === courseIdsKey) {
                    setNameSequence(sequence);
                }
            } catch (err) {
                console.error("Failed to fetch name sequence:", err);
                if (!cancelled) setNameSequence(null);
            }
        }, 300);
        return () => {
            cancelled = true;
            window.clearTimeout(timer);
        };
    }, [testType, courseIdsKey, subjectIds.length]);

    const handleCreateTest = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!testType || !trackId || !courseId || subjectIds.length === 0) {
            setAlertMessage("Track, course, test type, and subjects are required.");
            return;
        }
        const resolvedName = previewName.trim();
        if (!resolvedName) {
            setAlertMessage("Select type, course, and subjects to generate the test name.");
            return;
        }

        setLoading(true);
        setAlertMessage("");
        try {
            const response = await authService.post("/admin/tests/", {
                description: "",
                created_by: sessionStorage.getItem("Email"),
                test_type: testType,
                track_id: trackId,
                course_ids: [courseId],
                subject_ids: subjectIds,
                test_name: resolvedName,
            });
            const createdName = String(
                response.data?.test_name || resolvedName
            );
            sessionStorage.setItem("TestID", response.data.test_id);
            navigate(routes.configureDetail(createdName, response.data.test_id));
        } catch (error: unknown) {
            console.error("Error creating test:", error);
            const detail =
                (error as { response?: { data?: { detail?: string; message?: string } } })
                    ?.response?.data;
            setAlertMessage(
                detail?.detail || detail?.message || "An error occurred. Please try again."
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="h-100 d-flex flex-column">
            {alertMessage && (
                <Alert variant="danger" dismissible onClose={() => setAlertMessage("")}>
                    {alertMessage}
                </Alert>
            )}

            <div className="card shadow-sm flex-grow-1 border-0">
                <div className="card-body p-2">
                    <Form onSubmit={handleCreateTest}>
                        <Row className="g-3">
                            <Col xs={12} md={6}>
                                <TestFilterSelect
                                    label="Test Type *"
                                    value={testType}
                                    onChange={(value) => {
                                        setTestType(value);
                                        setSubjectIds([]);
                                    }}
                                    disabled={typesLoading}
                                    options={createableTypes.map((type) => ({
                                        value: type.test_type,
                                        label: type.test_type,
                                    }))}
                                    placeholder={
                                        typesLoading ? "Loading types..." : "Select type"
                                    }
                                    fullWidth
                                />
                            </Col>

                            <Col xs={12} md={6}>
                                <TestFilterSelect
                                    label="Track *"
                                    value={trackId}
                                    onChange={setTrackId}
                                    disabled={tracksLoading}
                                    options={tracks.map((t) => ({
                                        value: t.track_id,
                                        label: t.track_name,
                                    }))}
                                    placeholder={
                                        tracksLoading ? "Loading tracks..." : "Select track"
                                    }
                                    fullWidth
                                />
                            </Col>

                            <Col xs={12} md={6}>
                                <TestFilterSelect
                                    label="Course *"
                                    value={courseId}
                                    onChange={setCourseId}
                                    disabled={!trackId || coursesLoading}
                                    options={courseOptions}
                                    placeholder={
                                        coursesLoading ? "Loading courses..." : "Select course"
                                    }
                                    fullWidth
                                />
                            </Col>

                            <Col xs={12} md={6}>
                                <TestMultiSelect
                                    label={
                                        isOverallFinalTest
                                            ? "Subjects * (all included)"
                                            : "Subjects *"
                                    }
                                    value={subjectIds}
                                    onChange={
                                        isOverallFinalTest
                                            ? () => undefined
                                            : setSubjectIds
                                    }
                                    disabled={
                                        !trackId ||
                                        !courseId ||
                                        subjectsLoading
                                    }
                                    readOnly={isOverallFinalTest}
                                    options={subjectOptions}
                                    placeholder={
                                        subjectsLoading
                                            ? "Loading subjects..."
                                            : isOverallFinalTest
                                              ? "All subjects selected"
                                              : "Select subjects"
                                    }
                                    fullWidth
                                />
                                {isOverallFinalTest &&
                                    !subjectsLoading &&
                                    subjects.length > 0 && (
                                        <Form.Text className="text-muted">
                                            Overall Final Test includes every
                                            subject for this course. Subjects
                                            cannot be removed.
                                        </Form.Text>
                                    )}
                            </Col>

                            <Col xs={12} md={12}>
                                <TextField
                                    label="Test Name"
                                    value={previewName}
                                    size="small"
                                    fullWidth
                                    placeholder="Generated after type, course, and subjects are selected"
                                    InputLabelProps={{ shrink: true }}
                                    InputProps={{ readOnly: true }}
                                />
                            </Col>
                        </Row>

                        <div className="d-flex justify-content-end border-top pt-3">
                            <Button
                                variant="primary"
                                type="submit"
                                disabled={loading}
                                className="btn btn-sm px-4"
                            >
                                {loading ? (
                                    <Spinner animation="border" size="sm" />
                                ) : (
                                    "Create Test"
                                )}
                            </Button>
                        </div>
                    </Form>
                </div>
            </div>
        </div>
    );
};

export default CreateTestForm;
